import { useNavigate } from 'react-router-dom'
import { useQuery } from 'react-query'
import { useTheme } from 'next-themes'
import { ChevronsUpDown, LogOut, Mail, Monitor, Moon, Sun, UserCircle } from 'lucide-react'
import { useAuth } from '@/providers/auth'
import { initials } from '@/lib/format'
import { imageThumbnail } from '@/lib/imageUpload'
import { cn } from '@/lib/utils'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import { fetchPendingInvites, PENDING_INVITES_LIST_KEY } from '@/hooks/usePendingInvites'

export function usePendingInvitesCount(): number {
  const { data = 0 } = useQuery(PENDING_INVITES_LIST_KEY, fetchPendingInvites, { staleTime: 30_000, select: invites => invites.length })
  return data
}

const THEMES = [
  { value: 'light', label: 'Claro', icon: Sun },
  { value: 'dark', label: 'Escuro', icon: Moon },
  { value: 'system', label: 'Automático', icon: Monitor }
]

function ThemeMenu() {
  const { theme = 'system', setTheme } = useTheme()

  return (
    <>
      <DropdownMenuSeparator />
      <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">Tema</DropdownMenuLabel>
      <DropdownMenuRadioGroup value={theme} onValueChange={setTheme}>
        {THEMES.map(({ value, label, icon: Icon }) => (
          <DropdownMenuRadioItem key={value} value={value} className="gap-2">
            <Icon className="h-4 w-4" /> {label}
          </DropdownMenuRadioItem>
        ))}
      </DropdownMenuRadioGroup>
    </>
  )
}

export function UserMenu({ collapsed, onNavigate }: { collapsed?: boolean; onNavigate?: () => void }) {
  const { user, signOut } = useAuth()
  const navigate = useNavigate()
  const pendingInvites = usePendingInvitesCount()

  const go = (path: string) => {
    onNavigate?.()
    navigate(path)
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={pendingInvites > 0 ? `Menu da conta, ${pendingInvites} ${pendingInvites === 1 ? 'convite pendente' : 'convites pendentes'}` : 'Menu da conta'}
        className={cn(
          'relative flex min-w-0 items-center gap-2.5 rounded-lg text-left transition-colors hover:bg-sidebar-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring data-[state=open]:bg-sidebar-accent',
          collapsed ? 'h-10 w-10 justify-center' : 'w-full p-1.5 pr-2'
        )}
      >
        <Avatar className="h-8 w-8 rounded-lg">
          <AvatarImage src={imageThumbnail(user?.image, 96, 96)} alt="" className="object-cover" />
          <AvatarFallback className="rounded-lg bg-accent text-[11px] font-semibold text-accent-foreground">{initials(user?.name)}</AvatarFallback>
        </Avatar>
        {!collapsed && (
          <>
            <span className="min-w-0 flex-1 leading-tight">
              <span className="block truncate text-sm font-medium text-foreground">{user?.name || 'Minha conta'}</span>
              <span className="block truncate text-xs text-muted-foreground">{user?.email}</span>
            </span>
            <ChevronsUpDown className="h-4 w-4 shrink-0 text-muted-foreground" />
          </>
        )}
        {pendingInvites > 0 && <span aria-hidden className="absolute left-7 top-1 h-2.5 w-2.5 rounded-full border-2 border-sidebar bg-destructive" />}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" side={collapsed ? 'right' : 'top'} sideOffset={collapsed ? 10 : 6} className="w-60">
        <DropdownMenuLabel>
          <div className="truncate font-semibold">{user?.name}</div>
          <div className="truncate text-xs font-normal text-muted-foreground">{user?.email}</div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem className="gap-2" onClick={() => go('/usuario')}>
          <UserCircle className="h-4 w-4" /> Minha conta
        </DropdownMenuItem>
        <DropdownMenuItem className="gap-2" onClick={() => go('/usuario/convites')}>
          <Mail className="h-4 w-4" />
          <span className="flex-1">Convites</span>
          {pendingInvites > 0 && (
            <span className="tnum grid h-5 min-w-5 place-items-center rounded-full bg-destructive px-1.5 text-[11px] font-semibold text-destructive-foreground">
              {pendingInvites}
            </span>
          )}
        </DropdownMenuItem>
        <ThemeMenu />
        <DropdownMenuSeparator />
        <DropdownMenuItem variant="destructive" onClick={() => signOut(() => navigate('/login'))}>
          <LogOut className="h-4 w-4" /> Sair
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
